import { TranslateService } from '@ngx-translate/core';
import { IconType } from '@se/cp.common';
import { EcgExamination } from '@se/models';
import { GridUtilitiesService } from '../../services/grid-utilities.service';
import { EstensaColDef } from '../search-param.model';
import { DiffHeaderComponent } from '../search-result/header-renders/diff-header/diff-header.component';

export function ecgColumnDefs(translate: TranslateService, gridUtils: GridUtilitiesService): EstensaColDef[] {
    return [
        {
            colId: 'lockColId',
            headerName: '',
            tooltipField: '',
            width: 26,
            minWidth: 26,
            suppressSizeToFit: true,
            sortable: false,
            resizable: false,
            lockPosition: true,
            pinned: 'left',
            headerComponent: DiffHeaderComponent,
            cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', width: '26px' },
            cellRenderer: 'lockUnlockRenderer',
            cellRendererParams: {
                lockedField: 'locked',
                userField: 'lockedUserFullName',
            },
        },
        {
            colId: 'ExamOutOfFlow',
            headerName: '',
            valueGetter: (e) => e.data,
            tooltipField: '',
            width: 26,
            minWidth: 26,
            suppressSizeToFit: true,
            resizable: false,
            lockPosition: true,
            pinned: 'left',
            cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', width: '26px' },
            cellRenderer: 'stateIconRenderer',
            sortable: true,
            comparator: (valueA, valueB) => {
                if (valueA.patient.isLocalPatient && valueB.patient.isLocalPatient) return 0;
                if (!valueA.patient.isLocalPatient && !valueB.patient.isLocalPatient) return 0;
                if (!valueA.patient.isLocalPatient && valueB.patient.isLocalPatient) return 1;

                return -1;
            },
            cellRendererParams: {
                estensa: [
                    {
                        iconType: IconType.SeFont,
                        field: 'patient.isLocalPatient',
                        icon: 'icon_flusso_inverso',
                        tooltip: 'PatientDataNotAssociated',
                    },
                    {
                        iconType: IconType.AngularMaterial,
                        field: 'reportingWithoutTrace',
                        icon: 'image_not_supported',
                        fontSize: '18px',
                        tooltip: 'ExamWithoutTrace',
                    },
                    {
                        iconType: IconType.AngularMaterial,
                        field: 'fromIntegration',
                        icon: 'exit_to_app',
                        fontSize: '18px',
                        tooltip: 'ExternalIntegration',
                    },
                ],
            },
        },
        {
            colId: 'AssociatedVisit',
            headerName: '',
            valueGetter: (e) => e.data,
            width: 26,
            minWidth: 26,
            suppressSizeToFit: true,
            resizable: false,
            sortable: false,
            lockPosition: true,
            pinned: 'left',
            cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', width: '26px' },
            cellRenderer: 'stateIconRenderer',
            cellRendererParams: {
                estensa(data: EcgExamination) {
                    if (!data.isEcgReport && data.idReport != null) {
                        return {
                            iconType: IconType.AngularMaterial,
                            icon: 'link',
                            fontSize: '20px',
                            tooltip: translate.instant('VisitAndEcg'),
                        };
                    }
                    if (data.examinationsNumber && data.examinationsNumber > 1) {
                        return {
                            iconType: IconType.SeSvg,
                            icon: 'associations-visit',
                            tooltip: translate.instant('HasAssociatedExaminations'),
                        };
                    }
                },
            },
        },
        {
            colId: 'SameStudy',
            headerName: '',
            valueGetter: (e) => e.data,
            width: 26,
            minWidth: 26,
            suppressSizeToFit: true,
            resizable: false,
            sortable: false,
            lockPosition: true,
            pinned: 'left',
            cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', width: '26px' },
            cellRenderer: 'sameStudyRenderer',
            cellRendererParams: (params: any) => {
                return params;
            },
        },
        {
            colId: 'AttachedDocuments',
            headerName: '',
            valueGetter: (e) => e.data.hasAttachedDocuments,
            width: 26,
            minWidth: 26,
            suppressSizeToFit: true,
            resizable: false,
            sortable: false,
            lockPosition: true,
            pinned: 'left',
            cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', width: '26px' },
            cellRenderer: 'boolIconRenderer',
            cellRendererParams: (params: any) => {
                return {
                    estensa: {
                        iconType: IconType.AngularMaterial,
                        icon: 'file_present',
                        tooltip: translate.instant('AttachedDocuments'),
                    }
                };

            },
        },
        {
            colId: '0',
            headerName: translate.instant('Patient'),
            headerTooltip: translate.instant('Patient'),
            valueGetter: (e) => {
                const lastName = e.data.patient.lastName ?? '';
                const firstName = e.data.patient.firstName ?? '';
                return `${lastName.toLocaleUpperCase()} ${firstName.toLocaleUpperCase()}`;
            },
            tooltipValueGetter: (e) => {
                const lastName = e.data.patient.lastName ?? '';
                const firstName = e.data.patient.firstName ?? '';
                return `${lastName.toLocaleUpperCase()} ${firstName.toLocaleUpperCase()}`;
            },
            width: 220,
            minWidth: 220,
            suppressSizeToFit: true,
            lockPosition: true,
            pinned: 'left',
            cellStyle: (e) => {
                if (e.data.patient.isCertificating === true) return { 'background-color': 'yellow', color: 'black' };
                if (e.data.patient.isCertificating === false && e.data.patient.isCertified === false)
                    return { 'background-color': 'red', color: 'black' };
                return null;
            },
        },
        {
            colId: '1',
            headerName: translate.instant('BirthDate'),
            headerTooltip: translate.instant('BirthDate'),
            tooltipValueGetter: (p) => p.valueFormatted,
            field: 'patient.birthDate',
            width: 180,
            minWidth: 180,
            valueFormatter: gridUtils.dateFormatter(['patient', 'birthDate']),
            comparator: gridUtils.dateComparator,
        },
        {
            colId: '2',
            headerName: translate.instant('ExamDate'),
            headerTooltip: translate.instant('ExamDate'),
            tooltipValueGetter: (p) => p.valueFormatted,
            field: 'examinationDate',
            sort: 'desc',
            width: 150,
            minWidth: 150,
            valueFormatter: gridUtils.dateTimeFormatter('examinationDate'),
            comparator: gridUtils.dateComparator,
        },
        {
            colId: '14',
            headerName: translate.instant('AdmissionDate'),
            headerTooltip: translate.instant('AdmissionDate'),
            tooltipValueGetter: (p) => p.valueFormatted,
            field: 'admissionDate',
            width: 150,
            minWidth: 150,
            valueFormatter: gridUtils.dateTimeFormatter('admissionDate'),
            comparator: gridUtils.dateComparator,
            hide: true,
        },
        {
            colId: '15',
            headerName: translate.instant('RadioDiagnosis'),
            headerTooltip: translate.instant('RadioDiagnosis'),
            tooltipField: 'diagnosis.description',
            field: 'diagnosis.description',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            hide: true,
        },
        {
            colId: '3',
            headerName: translate.instant('Severity'),
            headerTooltip: translate.instant('Severity'),
            tooltipField: 'severity.description',
            field: 'severity.description',
            cellStyle: (params) => ({
                color:
                    params.data.severity?.color?.toLocaleLowerCase() == 'white'
                        ? 'black'
                        : params.data.severity?.color?.toLocaleLowerCase(),
            }),
            width: 100,
            minWidth: 100,
        },
        {
            colId: '4',
            headerName: translate.instant('Status'),
            headerTooltip: translate.instant('Status'),
            tooltipField: 'status',
            field: 'status',
            width: 80,
            minWidth: 80,
        },
        {
            colId: '5',
            headerName: translate.instant('Provenance'),
            headerTooltip: translate.instant('Provenance'),
            tooltipField: 'provenance.description',
            field: 'provenance.description',
            width: 150,
            minWidth: 150,
        },
        {
            colId: '6',
            headerName: translate.instant('ReferringPhysician'),
            headerTooltip: translate.instant('ReferringPhysician'),
            valueGetter: (e) => gridUtils.getFullName(e.data.referringPhysician),
            tooltipValueGetter: (e) => gridUtils.getFullName(e.data.referringPhysician),
            width: 220,
            minWidth: 220,
        },
        {
            colId: '7',
            headerName: translate.instant('ReportDate'),
            headerTooltip: translate.instant('ReportDate'),
            tooltipValueGetter: (p) => p.valueFormatted,
            field: 'reportDate',
            width: 150,
            minWidth: 150,
            valueFormatter: gridUtils.dateTimeFormatter('reportDate'),
            comparator: gridUtils.dateComparator,
        },
        {
            colId: '8',
            headerName: translate.instant('ExamNumber'),
            headerTooltip: translate.instant('ExamNumber'),
            tooltipField: 'examNumber',
            field: 'examNumber',
            width: 190,
            minWidth: 190,
        },

        {
            colId: '9',
            headerName: translate.instant('Accession_Number'),
            headerTooltip: translate.instant('Accession_Number'),
            field: 'accessionNumber',
            width: 190,
            minWidth: 190,
        },
        {
            colId: '10',
            headerName: translate.instant('Exam'),
            headerTooltip: translate.instant('Exam'),
            tooltipField: 'exam.description',
            field: 'exam.description',
            width: 220,
            minWidth: 220,
        },
        {
            colId: '11',
            headerName: translate.instant('BusinessUnit'),
            headerTooltip: translate.instant('BusinessUnit'),
            tooltipField: 'businessUnit.description',
            field: 'businessUnit.description',
            width: 190,
            minWidth: 190,
        },
        {
            colId: '12',
            headerName: translate.instant('PatientId'),
            headerTooltip: translate.instant('PatientId'),
            tooltipField: 'patient.patientId',
            field: 'patient.patientId',
            width: 190,
            minWidth: 190,
        },
        {
            headerName: translate.instant('Equipment'),
            headerTooltip: translate.instant('Equipment'),
            tooltipField: 'equipment.description',
            field: 'equipment.description',
            colId: '13',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            hide: false,
        },
        {
            headerName: translate.instant('OtherPatientId'),
            headerTooltip: translate.instant('OtherPatientId'),
            tooltipField: 'patient.otherPatientId',
            field: 'patient.otherPatientId',
            colId: '16',
            width: 120,
            minWidth: 120,
            suppressSizeToFit: false,
            hide: false,
        },
    ];
}


